import { useEffect, useState, useRef } from "react";
import {
  TextField,
  Button,
  Card,
  Typography,
  Grid,
  Box,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import Papa from "papaparse";
import { FaUpload, FaTrash, FaPlus, FaChevronDown } from "react-icons/fa";
import { createChapters } from "../Servies/AdmiServices";

export default function CreateChapter() {
  const location = useLocation();
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [projectName, setProjectName] = useState("");

  const queryParams = new URLSearchParams(location.search);
  const pId = queryParams.get("projectId");
  const pName = queryParams.get("projectName");

  const emptyChapter = {
    chapterName: "",
    designerName: "",
    noOfPpt: "",
    noOfVideo: "",
    pptVideoStartDate: "",
    pptVideoEndDate: "",
    microplanRecieved: "",
    microplanName: "",
    microplanDate: "",
    microplanClassworkDate: "",
    pdfResource: "",
    recordingTeacher: "",
    recordingDate: "",
    contents: [], // Array of contents for this chapter
  };

  const emptyContent = {
    contentTitle: "",
    contentType: "",
    contentUrl: "",
    duration: "",
    sequenceNumber: "",
    description: "",
  };

  const [chapters, setChapters] = useState([emptyChapter]);
  const [expandedChapters, setExpandedChapters] = useState({}); // Track which chapters are expanded
  const fileInputRef = useRef(null);
  const contentFileInputRef = useRef(null);

  // Required fields for CSV validation
  const requiredFields = [
    "chapterName",
    "designerName",
    "noOfPpt",
    "noOfVideo"
  ];

useEffect(() => {
  if (pId) setProjectId(pId);
  if (pName) setProjectName(decodeURIComponent(pName));
  else toast.error("Project name not found!");
}, [pId, pName]);

  // Handle field change
  const handleChange = (index, field, value) => {
    const updated = [...chapters];
    updated[index][field] = value;
    setChapters(updated);
  };

    // --- Handle Cancel ---
const handleBack = () => {
  navigate(`/dashboard/ProjectChapters?projectId=${pId}`);
};

  // Add new chapter row
  const handleAddRow = () => setChapters([...chapters, emptyChapter]);

  // Delete chapter row
  const handleDeleteRow = (index) => {
    if (chapters.length === 1) {
      toast.warning("Cannot delete the last chapter");
      return;
    }
    const updated = chapters.filter((_, i) => i !== index);
    setChapters(updated);
    toast.success("Chapter removed");
  };

  // Add content to a specific chapter
  const handleAddContent = (chapterIndex) => {
    const updated = [...chapters];
    updated[chapterIndex].contents.push({ ...emptyContent });
    setChapters(updated);
    toast.success("Content added");
  };

  // Delete content from a specific chapter
  const handleDeleteContent = (chapterIndex, contentIndex) => {
    const updated = [...chapters];
    updated[chapterIndex].contents.splice(contentIndex, 1);
    setChapters(updated);
    toast.success("Content removed");
  };

  // Handle content field change
  const handleContentChange = (chapterIndex, contentIndex, field, value) => {
    const updated = [...chapters];
    updated[chapterIndex].contents[contentIndex][field] = value;
    setChapters(updated);
  };

  // Toggle chapter accordion
  const handleToggleChapter = (index) => {
    setExpandedChapters(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Handle CSV upload
  const handleCSVUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const parsedData = results.data;

          if (parsedData.length === 0) {
            toast.error('CSV file is empty');
            return;
          }

          // Validate all required fields are present in CSV headers
          const csvHeaders = Object.keys(parsedData[0]);
          const missingFields = requiredFields.filter(
            (field) => !csvHeaders.includes(field)
          );

          if (missingFields.length > 0) {
            toast.error(
              `Missing required fields in CSV: ${missingFields.join(', ')}`
            );
            return;
          }

          // Validate each row for mandatory fields
          const errors = [];
          const validChapters = [];

          parsedData.forEach((row, index) => {
            const rowErrors = [];

            requiredFields.forEach((field) => {
              if (!row[field] || row[field].trim() === '') {
                rowErrors.push(field);
              }
            });

            if (rowErrors.length > 0) {
              errors.push(
                `Row ${index + 1}: Missing values for ${rowErrors.join(', ')}`
              );
            } else {
              validChapters.push({
                chapterName: row.chapterName,
                designerName: row.designerName,
                noOfPpt: row.noOfPpt,
                noOfVideo: row.noOfVideo
              });
            }
          });

          if (errors.length > 0) {
            toast.error(
              <div>
                <div>Invalid CSV data:</div>
                {errors.slice(0, 3).map((err, i) => (
                  <div key={i}>{err}</div>
                ))}
                {errors.length > 3 && <div>...and {errors.length - 3} more errors</div>}
              </div>,
              { autoClose: 8000 }
            );
            return;
          }

          // Set the valid chapters
          setChapters(validChapters);
          toast.success(`Successfully loaded ${validChapters.length} chapters from CSV`);

          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } catch (error) {
          console.error('CSV parsing error:', error);
          toast.error('Error parsing CSV file');
        }
      },
      error: (error) => {
        console.error('CSV file error:', error);
        toast.error('Error reading CSV file');
      },
    });
  };

  // Trigger file input click
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle CSV upload for contents
  const handleContentCSVUpload = (event, chapterIndex) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const parsedData = results.data;

          if (parsedData.length === 0) {
            toast.error('CSV file is empty');
            return;
          }

          const requiredContentFields = ['contentTitle', 'contentType'];
          const csvHeaders = Object.keys(parsedData[0]);
          const missingFields = requiredContentFields.filter(
            (field) => !csvHeaders.includes(field)
          );

          if (missingFields.length > 0) {
            toast.error(
              `Missing required fields in CSV: ${missingFields.join(', ')}`
            );
            return;
          }

          const validContents = parsedData.map(row => ({
            contentTitle: row.contentTitle || '',
            contentType: row.contentType || '',
            contentUrl: row.contentUrl || '',
            duration: row.duration || '',
            sequenceNumber: row.sequenceNumber || '',
            description: row.description || '',
          }));

          const updated = [...chapters];
          updated[chapterIndex].contents = validContents;
          setChapters(updated);

          toast.success(`Successfully loaded ${validContents.length} contents from CSV`);

          if (contentFileInputRef.current) {
            contentFileInputRef.current.value = '';
          }
        } catch (error) {
          console.error('CSV parsing error:', error);
          toast.error('Error parsing CSV file');
        }
      },
      error: (error) => {
        console.error('CSV file error:', error);
        toast.error('Error reading CSV file');
      },
    });
  };

  // Trigger content CSV file input click
  const [currentChapterIndex, setCurrentChapterIndex] = useState(null);
  const handleContentUploadClick = (chapterIndex) => {
    setCurrentChapterIndex(chapterIndex);
    contentFileInputRef.current?.click();
  };

  // Submit data
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectId) {
      toast.error("Project ID is missing.");
      return;
    }

    setIsLoading(true);
    const payload = chapters.map((ch) => ({ ...ch, projectId }));

    try {
      const res = await createChapters(payload);
      if (res.data?.status === "SUCCESS") {
        toast.success(res.data?.message || "Chapters added successfully!");
        setChapters([emptyChapter]);

        // Navigate after 1.5 seconds
        setTimeout(() => {
          const prevUrl = document.referrer;
          if (prevUrl.includes("/ProjectChapters")) {
            window.location.href = prevUrl;
          } else if (projectId) {
            navigate(`/dashboard/ProjectChapters?projectId=${projectId}`);
          } else {
            navigate(-1);
          }
        }, 1500);
      } else {
        toast.error(res.data?.message || "Something went wrong!");
      }
    } catch (error) {
      toast.error("Error adding chapters");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ToastContainer />
      {/* --- Back Button --- */}
      <Box sx={{ padding: "20px 40px" }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleBack}
          sx={{
            borderColor: "#ccc",
            "&:hover": { borderColor: "#888" },
          }}
        >
          ← Back 
        </Button>
      </Box>
    <Typography variant="h4"  fontWeight={600} marginLeft={5}>
   Add Chapters for: {projectName || "N/A"} 
</Typography>

      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <Card
          sx={{
            p: 5,
            width: "100%",
            maxWidth: 1100,
            boxShadow: 4,
            backgroundColor: "#fff",
            borderRadius: "16px",
          }}
        >
          {/* Hidden file input for chapters CSV */}
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleCSVUpload}
          />

          {/* Hidden file input for contents CSV */}
          <input
            type="file"
            ref={contentFileInputRef}
            accept=".csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              if (currentChapterIndex !== null) {
                handleContentCSVUpload(e, currentChapterIndex);
              }
            }}
          />

          {/* Upload CSV Button */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FaUpload />}
              onClick={handleUploadClick}
              sx={{ textTransform: 'none' }}
            >
              Upload CSV
            </Button>
          </Box>

          <form onSubmit={handleSubmit}>
            {chapters.map((chapter, index) => (
              <Box
                key={index}
                mb={4}
                sx={{ borderBottom: "1px solid #eee", pb: 3 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">
                    Chapter {index + 1}
                  </Typography>
                  {chapters.length > 1 && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<FaTrash />}
                      onClick={() => handleDeleteRow(index)}
                      sx={{ textTransform: 'none' }}
                    >
                      Delete
                    </Button>
                  )}
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Chapter Name"
                      fullWidth
                      value={chapter.chapterName}
                      onChange={(e) =>
                        handleChange(index, "chapterName", e.target.value)
                      }
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Designer Name"
                      fullWidth
                      value={chapter.designerName}
                      onChange={(e) =>
                        handleChange(index, "designerName", e.target.value)
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="No. of PPT"
                      type="number"
                      fullWidth
                      value={chapter.noOfPpt}
                      onChange={(e) =>
                        handleChange(index, "noOfPpt", e.target.value)
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="No. of Videos"
                      type="number"
                      fullWidth
                      value={chapter.noOfVideo}
                      onChange={(e) =>
                        handleChange(index, "noOfVideo", e.target.value)
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Microplan Name"
                      fullWidth
                      value={chapter.microplanName}
                      onChange={(e) =>
                        handleChange(index, "microplanName", e.target.value)
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Recording Teacher"
                      fullWidth
                      value={chapter.recordingTeacher}
                      onChange={(e) =>
                        handleChange(index, "recordingTeacher", e.target.value)
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Recording Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      value={chapter.recordingDate}
                      onChange={(e) =>
                        handleChange(index, "recordingDate", e.target.value)
                      }
                    />
                  </Grid>
                </Grid>

                {/* Chapter Contents Section */}
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Accordion
                    expanded={expandedChapters[index] || false}
                    onChange={() => handleToggleChapter(index)}
                    sx={{ boxShadow: 2, borderRadius: 1 }}
                  >
                    <AccordionSummary expandIcon={<FaChevronDown />}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="h6" fontWeight={600}>
                          Chapter Contents
                        </Typography>
                        <Chip
                          label={`${chapter.contents?.length || 0} contents`}
                          size="small"
                          color="primary"
                        />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        {/* CSV Upload for Contents */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FaUpload />}
                            onClick={() => handleContentUploadClick(index)}
                            sx={{ textTransform: 'none' }}
                          >
                            Upload Contents CSV
                          </Button>
                        </Box>

                        {/* Content List */}
                        {chapter.contents && chapter.contents.length > 0 ? (
                          chapter.contents.map((content, contentIndex) => (
                            <Card key={contentIndex} sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="subtitle2" color="primary">
                                  Content {contentIndex + 1}
                                </Typography>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteContent(index, contentIndex)}
                                >
                                  <FaTrash />
                                </IconButton>
                              </Stack>

                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label="Content Title"
                                    fullWidth
                                    size="small"
                                    value={content.contentTitle}
                                    onChange={(e) =>
                                      handleContentChange(index, contentIndex, "contentTitle", e.target.value)
                                    }
                                    required
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label="Content Type"
                                    fullWidth
                                    size="small"
                                    value={content.contentType}
                                    onChange={(e) =>
                                      handleContentChange(index, contentIndex, "contentType", e.target.value)
                                    }
                                    placeholder="e.g., Video, PPT, PDF"
                                    required
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label="Content URL"
                                    fullWidth
                                    size="small"
                                    value={content.contentUrl}
                                    onChange={(e) =>
                                      handleContentChange(index, contentIndex, "contentUrl", e.target.value)
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                  <TextField
                                    label="Duration (mins)"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={content.duration}
                                    onChange={(e) =>
                                      handleContentChange(index, contentIndex, "duration", e.target.value)
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                  <TextField
                                    label="Sequence #"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={content.sequenceNumber}
                                    onChange={(e) =>
                                      handleContentChange(index, contentIndex, "sequenceNumber", e.target.value)
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <TextField
                                    label="Description"
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={2}
                                    value={content.description}
                                    onChange={(e) =>
                                      handleContentChange(index, contentIndex, "description", e.target.value)
                                    }
                                  />
                                </Grid>
                              </Grid>
                            </Card>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            No contents added yet. Click "Add Content" or upload CSV.
                          </Typography>
                        )}

                        {/* Add Content Button */}
                        <Button
                          variant="outlined"
                          startIcon={<FaPlus />}
                          onClick={() => handleAddContent(index)}
                          sx={{ textTransform: 'none' }}
                        >
                          Add Content
                        </Button>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Box>
            ))}

            <Stack direction="row" justifyContent="space-between" mt={3}>
              <Button variant="outlined" onClick={handleAddRow}>
                + Add Chapter
              </Button>

              <Button type="submit" variant="contained" disabled={isLoading}>
                {isLoading ? "Submitting..." : "Submit"}
              </Button>
            </Stack>
          </form>
        </Card>
      </Box>
    </>
  );
}
